/**
 * MagicLogin — consumes a magic-link token from the URL hash and signs the
 * user in (sets the access cookie via /auth/magic-link/consume) before
 * redirecting to /agency-dashboard.
 *
 * URL shape: /magic-login#token=eyJ...
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MagicLogin() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
    const token = new URLSearchParams(hash).get("token");
    if (!token) {
      setError("Magic link is missing its token.");
      return;
    }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/magic-link/consume`, { token });
        // Persist access token for axios interceptors / ProtectedRoute.
        localStorage.setItem("alp.token", data.access_token);
        // Clear the URL hash so the token doesn't sit in browser history.
        window.history.replaceState(null, "", "/magic-login");
        navigate("/agency-dashboard", { replace: true });
      } catch (err) {
        setError(err?.response?.data?.detail || err.message || "Could not validate magic link");
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A1233] text-white p-6"
         data-testid="magic-login-page">
      <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-8 max-w-md w-full text-center">
        {error ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-rose-500/20 text-rose-300 flex items-center justify-center mb-3">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <h1 className="text-[18px] font-bold">Magic link invalid</h1>
            <p className="text-[13px] text-white/65 mt-2">{error}</p>
            <a href="/login" className="inline-block mt-4 px-4 h-10 rounded-lg bg-[#C9A24E] hover:bg-[#b48d3e] text-[#1A0E36] text-[12.5px] font-bold leading-10">
              Back to login
            </a>
          </>
        ) : (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center mb-3">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <h1 className="text-[18px] font-bold">Signing you in…</h1>
            <p className="text-[13px] text-white/65 mt-2 inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Validating your magic link
            </p>
          </>
        )}
      </div>
    </div>
  );
}
